import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  BookOpen,
  Target,
  TrendingUp,
  Play,
  Brain,
  Users
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import procesoService from '../../services/procesoService';
import usuarioExamenService from '../../services/usuarioExamenService';
import perfilPruebaService from '../../services/perfilPruebaService';
import discService from '../../services/discService';
import ipvService from '../../services/ipvService';
import useAuthStore from '../../store/authStore';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Loader from '../../components/ui/Loader';
import MobileWarning from '../../components/common/MobileWarning';
import RealizarExamen from './RealizarExamen';
import RealizarExamenRaven from './RealizarExamenRaven';
import RealizarTestDisc from './RealizarTestDisc';
import RealizarExamenIpv from './RealizarExamenIpv';

const MisTests = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [procesos, setProcesos] = useState([]);
  const [examenActivo, setExamenActivo] = useState(null);
  const [testDiscActivo, setTestDiscActivo] = useState(null);
  const [examenIpvActivo, setExamenIpvActivo] = useState(null);
  const [estadosExamenes, setEstadosExamenes] = useState({});
  const [testsDiscDisponibles, setTestsDiscDisponibles] = useState([]);
  const [testsDiscCompletados, setTestsDiscCompletados] = useState([]);
  const [examenesIpvDisponibles, setExamenesIpvDisponibles] = useState([]);
  const [examenesIpvCompletados, setExamenesIpvCompletados] = useState([]);
  const [asociacionesPruebas, setAsociacionesPruebas] = useState([]);

  useEffect(() => {
    loadMisProcesos();
  }, []);

  const loadMisProcesos = async () => {
    try {
      setLoading(true);
      
      // Obtener los procesos asignados al usuario directamente
      const misProcesos = await procesoService.getProcesosPorUsuario(user.id);
      
      setProcesos(misProcesos);

      // Cargar las asociaciones de perfil-pruebas del usuario
      const asociaciones = await perfilPruebaService.getPruebasPorUsuario(user.id);
      setAsociacionesPruebas(asociaciones);

      // Cargar estados de todos los exámenes del usuario
      await cargarEstadosExamenes(misProcesos);

      // Cargar tests DISC disponibles para el usuario (pasando asociaciones)
      await cargarTestsDiscDisponibles(asociaciones);

      // Cargar tests DISC completados por el usuario
      await cargarTestsDiscCompletados();

      // Cargar exámenes IPV disponibles para el usuario (pasando asociaciones)
      await cargarExamenesIpvDisponibles(asociaciones);

      // Cargar exámenes IPV completados por el usuario
      await cargarExamenesIpvCompletados();
    } catch (error) {
      console.error('Error al cargar mis procesos:', error);
      if (error.response?.status !== 400) {
        toast.error('Error al cargar tus exámenes');
      }
      setProcesos([]);
    } finally {
      setLoading(false);
    }
  };

  const cargarEstadosExamenes = async (procesos) => {
    try {
      // Obtener todos los exámenes del usuario
      const todosLosExamenes = await usuarioExamenService.obtenerExamenesUsuario(user.id);
      
      // Crear un mapa de examenId -> estado
      const estadosMap = {};
      todosLosExamenes.forEach(examen => {
        estadosMap[examen.examenId] = {
          estado: examen.estado,
          puntajeObtenido: examen.puntajeObtenido,
          puntajeMaximo: examen.puntajeMaximo,
          porcentajeCompletado: examen.porcentajeCompletado
        };
      });
      
      setEstadosExamenes(estadosMap);
    } catch (error) {
      console.error('Error al cargar estados de exámenes:', error);
      // No mostrar error al usuario, solo log
    }
  };

  const actualizarSoloEstados = async () => {
    try {
      // Solo recargar estados de exámenes
      await cargarEstadosExamenes([]);
      await cargarTestsDiscCompletados();
      await cargarExamenesIpvCompletados();
    } catch (error) {
      console.error('Error al actualizar estados:', error);
    }
  };

  const cargarTestsDiscDisponibles = async (asociaciones = asociacionesPruebas) => {
    try {
      // Filtrar solo las pruebas DISC del usuario (que tienen discExamenId o testDiscId)
      const pruebasDisc = asociaciones.filter(
        prueba => prueba.testDiscId != null || prueba.discExamenId != null
      );
      
      console.log('📊 Tests DISC disponibles:', pruebasDisc);
      setTestsDiscDisponibles(pruebasDisc);
    } catch (error) {
      console.error('Error al cargar tests DISC:', error);
      // No mostrar error al usuario
    }
  };

  const cargarTestsDiscCompletados = async () => {
    try {
      // Verificar si el usuario ya completó el test DISC
      const testsCompletados = await discService.getTestsCompletadosPorUsuario(user.id);
      console.log('✅ Tests DISC completados por el usuario:', testsCompletados);
      setTestsDiscCompletados(testsCompletados);
    } catch (error) {
      console.error('Error al cargar tests DISC completados:', error);
      // Si da error 404, significa que no ha completado ningún test
      setTestsDiscCompletados([]);
    }
  };

  const cargarExamenesIpvDisponibles = async (asociaciones = asociacionesPruebas) => {
    try {
      // Filtrar solo las pruebas IPV del usuario (que tienen ipvExamenId)
      const pruebasIpv = asociaciones.filter(
        prueba => prueba.ipvExamenId != null
      );
      
      console.log('📊 Exámenes IPV disponibles:', pruebasIpv);
      setExamenesIpvDisponibles(pruebasIpv);
    } catch (error) {
      console.error('Error al cargar exámenes IPV:', error);
      // No mostrar error al usuario
    }
  };

  const cargarExamenesIpvCompletados = async () => {
    try {
      // Obtener evaluaciones IPV completadas por el usuario
      const evaluacionesUsuario = await ipvService.getExamenesCompletadosPorUsuario(user.id);
      console.log('✅ Todos los exámenes IPV del usuario:', evaluacionesUsuario);
      
      // Filtrar solo los que están FINALIZADOS
      const finalizados = evaluacionesUsuario.filter(ex => ex.estado === 'FINALIZADO');
      console.log('✅ Exámenes IPV FINALIZADOS:', finalizados);
      
      setExamenesIpvCompletados(finalizados);
    } catch (error) {
      console.error('Error al cargar exámenes IPV completados:', error);
      setExamenesIpvCompletados([]);
    }
  };

  const iniciarExamen = (examen, perfil, proceso) => {
    console.log('🎯 Iniciando examen:', {
      nombre: examen.nombre,
      tipoExamen: examen.tipoExamen,
      id: examen.id,
      preguntas: examen.preguntas?.length
    });
    
    setExamenActivo({
      examen,
      perfil,
      proceso
    });
  };

  const iniciarTestDisc = (proceso) => {
    setTestDiscActivo({
      proceso
    });
  };

  const iniciarExamenIpv = (proceso) => {
    setExamenIpvActivo({
      proceso
    });
  };

  const finalizarExamen = () => {
    setExamenActivo(null);
    actualizarSoloEstados(); // Solo actualizar estados sin recargar todo
  };

  const finalizarTestDisc = () => {
    setTestDiscActivo(null);
    actualizarSoloEstados(); // Solo actualizar estados sin recargar todo
  };

  const finalizarExamenIpv = () => {
    setExamenIpvActivo(null);
    actualizarSoloEstados(); // Solo actualizar estados sin recargar todo
  };

  // Función helper para obtener exámenes Raven de un perfil
  // Los exámenes ya vienen dentro de perfil.examenes desde el backend
  const getExamenesRavenPorPerfil = (perfil) => {
    // Retornar directamente los exámenes del perfil
    return perfil.examenes || [];
  };

  if (testDiscActivo) {
    return (
      <RealizarTestDisc
        discExamen={null}
        proceso={testDiscActivo.proceso}
        onFinalizar={finalizarTestDisc}
      />
    );
  }

  if (examenIpvActivo) {
    return (
      <RealizarExamenIpv
        proceso={examenIpvActivo.proceso}
        onFinalizar={finalizarExamenIpv}
      />
    );
  }

  if (examenActivo) {
    // Detectar si es un examen tipo RAVEN para usar el componente específico
    const esExamenRaven = examenActivo.examen.tipoExamen === 'RAVEN';
    
    if (esExamenRaven) {
      console.log('🎯 Renderizando examen RAVEN con formato de lista');
      return (
        <RealizarExamenRaven
          examen={examenActivo.examen}
          perfil={examenActivo.perfil}
          proceso={examenActivo.proceso}
          onFinalizar={finalizarExamen}
        />
      );
    }
    
    console.log('📝 Renderizando examen tradicional pregunta por pregunta');
    return (
      <RealizarExamen
        examen={examenActivo.examen}
        perfil={examenActivo.perfil}
        proceso={examenActivo.proceso}
        onFinalizar={finalizarExamen}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader size="lg" />
      </div>
    );
  }

  const getTotalExamenes = () => {
    return procesos.reduce((total, proceso) => {
      return total + (proceso.perfiles?.reduce((sum, perfil) => {
        return sum + (perfil.examenes?.length || 0);
      }, 0) || 0);
    }, 0);
  };

  const getExamenesCompletados = () => {
    // Esto se debe implementar cuando tengas el endpoint de resultados
    return 0;
  };

  return (
    <MobileWarning>
      <div className="space-y-6">
        {/* Encabezado */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Exámenes</h1>
            <p className="text-neutral-600 mt-1">
              Realiza tus evaluaciones asignadas
            </p>
          </div>
        </div>

      

      {/* Lista de procesos y exámenes */}
      {procesos.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              No tienes exámenes asignados
            </h3>
            <p className="text-neutral-600">
              Contacta a tu supervisor para que te asigne un proceso
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {procesos.map((proceso) => (
            <Card key={proceso.id}>

              {/* Perfiles y Exámenes */}
              <div className="space-y-6">
                {proceso.perfiles && proceso.perfiles.length > 0 ? (
                  proceso.perfiles.map((perfil) => (
                    <div key={perfil.id} className="space-y-3">
                      {/* Header del Perfil */}
                      

                      {/* Grilla unificada de todos los exámenes */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Exámenes Raven del Perfil */}
                        {getExamenesRavenPorPerfil(perfil).map((examen) => (
                          <motion.div
                            key={`raven-${examen.id}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            whileHover={{ y: -4 }}
                            className="relative bg-white border-2 border-orange-200 rounded-xl overflow-hidden hover:shadow-xl hover:border-orange-300 transition-all duration-300 group"
                          >
                            {/* Header con gradiente */}
                            <div className="bg-gradient-to-br from-orange-500 to-amber-500 p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                                  <Target className="w-7 h-7 text-white" />
                                </div>
                                <div className="flex-1">
                                  <h3 className="font-bold text-white text-lg">Test Raven</h3>
                                  <p className="text-orange-100 text-xs">Razonamiento Abstracto</p>
                                </div>
                              </div>
                            </div>

                            {/* Estado Badge */}
                            {(estadosExamenes[examen.id]?.estado === 'FINALIZADO' || 
                              estadosExamenes[examen.id]?.estado === 'COMPLETADO') && (
                              <div className="absolute top-4 right-4 z-10">
                                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-lg">
                                  <CheckCircle className="w-3 h-3" />
                                  Completado
                                </div>
                              </div>
                            )}

                            {/* Contenido */}
                            <div className="p-5">
                              <div className="space-y-3 mb-5">
                                <p className="text-sm text-neutral-600 mb-4 leading-relaxed">
                                  Evaluación de perfil de personalidad y comportamiento laboral
                                </p>
                                <div className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
                                  
                                  <div className="flex items-center gap-2 text-sm text-neutral-700">
                                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                                      <Clock className="w-4 h-4 text-orange-600" />
                                    </div>
                                    <span className="font-medium">Duración</span>
                                  </div>
                                  <span className="font-bold text-orange-600">{examen.tiempoMinutos} min</span>
                                </div>
                                
                                <div className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
                                  <div className="flex items-center gap-2 text-sm text-neutral-700">
                                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                                      <FileText className="w-4 h-4 text-orange-600" />
                                    </div>
                                    <span className="font-medium">Preguntas</span>
                                  </div>
                                  <span className="font-bold text-orange-600">{examen.preguntas?.length || 0}</span>
                                </div>

                                
                              </div>

                              <Button
                                variant="primary"
                                size="md"
                                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
                                onClick={() => {
                                  const examenRaven = { ...examen, tipoExamen: 'RAVEN' };
                                  iniciarExamen(examenRaven, perfil, proceso);
                                }}
                                disabled={
                                  !examen.preguntas || 
                                  examen.preguntas.length === 0 || 
                                  estadosExamenes[examen.id]?.estado === 'FINALIZADO' ||
                                  estadosExamenes[examen.id]?.estado === 'COMPLETADO'
                                }
                              >
                                <Play className="w-5 h-5" />
                                {!examen.preguntas || examen.preguntas.length === 0 
                                  ? 'Sin preguntas' 
                                  : (estadosExamenes[examen.id]?.estado === 'FINALIZADO' || estadosExamenes[examen.id]?.estado === 'COMPLETADO')
                                  ? 'Examen Finalizado'
                                  : estadosExamenes[examen.id]?.estado === 'EN_PROGRESO'
                                  ? 'Continuar Examen'
                                  : 'Iniciar Examen Raven'
                                }
                              </Button>
                            </div>
                          </motion.div>
                        ))}

                        {/* Tests DISC del Perfil */}
                        {testsDiscDisponibles
                          .filter(td => td.perfilId === perfil.id)
                          .map((testDisc) => (
                            <motion.div
                              key={`disc-${testDisc.id}`}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              whileHover={{ y: -4 }}
                              className="relative bg-white border-2 border-purple-200 rounded-xl overflow-hidden hover:shadow-xl hover:border-purple-300 transition-all duration-300 group"
                            >
                              {/* Header con gradiente */}
                              <div className="bg-gradient-to-br from-purple-600 to-pink-600 p-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <Brain className="w-7 h-7 text-white" />
                                  </div>
                                  <div className="flex-1">
                                    <h3 className="font-bold text-white text-lg">Test DISC</h3>
                                    <p className="text-purple-100 text-xs">Perfil de Personalidad</p>
                                  </div>
                                </div>
                              </div>

                              {/* Estado Badge */}
                              {testsDiscCompletados.length > 0 && (
                                <div className="absolute top-4 right-4 z-10">
                                  <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-lg">
                                    <CheckCircle className="w-3 h-3" />
                                    Completado
                                  </div>
                                </div>
                              )}

                              {/* Contenido */}
                              <div className="p-5">
                                <p className="text-sm text-neutral-600 mb-4 leading-relaxed">
                                  Evaluación de perfil de personalidad y comportamiento laboral
                                </p>

                                <div className="space-y-3 mb-5">
                                  <div className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                                    <div className="flex items-center gap-2 text-sm text-neutral-700">
                                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                        <FileText className="w-4 h-4 text-purple-600" />
                                      </div>
                                      <span className="font-medium">Grupos</span>
                                    </div>
                                    <span className="font-bold text-purple-600">28 palabras</span>
                                  </div>

                                  <div className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                                    <div className="flex items-center gap-2 text-sm text-neutral-700">
                                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                        <Clock className="w-4 h-4 text-purple-600" />
                                      </div>
                                      <span className="font-medium">Duración</span>
                                    </div>
                                    <span className="font-bold text-purple-600">10-15 min</span>
                                  </div>

                                  {/*<div className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                                    <div className="flex items-center gap-2 text-sm text-neutral-700">
                                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                        <Target className="w-4 h-4 text-purple-600" />
                                      </div>
                                      <span className="font-medium">Perfil</span>
                                    </div>
                                    <Badge variant="secondary" className="bg-purple-100 text-purple-700 border-purple-200">
                                      {testDisc.perfilNombre}
                                    </Badge>
                                  </div>
                                   */}
                                </div>
                                 

                                <Button
                                  variant="primary"
                                  size="md"
                                  className={testsDiscCompletados.length > 0 
                                    ? "w-full bg-neutral-100 text-neutral-400 cursor-not-allowed" 
                                    : "w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
                                  }
                                  onClick={() => iniciarTestDisc(proceso)}
                                  disabled={testsDiscCompletados.length > 0}
                                >
                                  <Brain className="w-5 h-5" />
                                  {testsDiscCompletados.length > 0 ? 'Test Finalizado' : 'Iniciar Test DISC'}
                                </Button>
                              </div>
                            </motion.div>
                          ))}

                        {/* Exámenes IPV del Perfil */}
                        {examenesIpvDisponibles
                          .filter(ipv => ipv.perfilId === perfil.id)
                          .map((examenIpv) => (
                            <motion.div
                              key={`ipv-${examenIpv.id}`}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              whileHover={{ y: -4 }}
                              className="relative bg-white border-2 border-indigo-200 rounded-xl overflow-hidden hover:shadow-xl hover:border-indigo-300 transition-all duration-300 group"
                            >
                              {/* Header con gradiente */}
                              <div className="bg-gradient-to-br from-indigo-600 to-purple-600 p-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <Users className="w-7 h-7 text-white" />
                                  </div>
                                  <div className="flex-1">
                                    <h3 className="font-bold text-white text-lg">Examen IPV</h3>
                                    <p className="text-indigo-100 text-xs">Perfil de Vendedor</p>
                                  </div>
                                </div>
                              </div>

                              {/* Estado Badge */}
                              {examenesIpvCompletados.length > 0 && (
                                <div className="absolute top-4 right-4 z-10">
                                  <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-lg">
                                    <CheckCircle className="w-3 h-3" />
                                    Completado
                                  </div>
                                </div>
                              )}

                              {/* Contenido */}
                              <div className="p-5">
                                <p className="text-sm text-neutral-600 mb-4 leading-relaxed">
                                  Evaluación de características de personalidad para ventas
                                </p>

                                <div className="space-y-3 mb-5">
                                  <div className="flex items-center justify-between p-2 bg-indigo-50 rounded-lg">
                                    <div className="flex items-center gap-2 text-sm text-neutral-700">
                                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                                        <FileText className="w-4 h-4 text-indigo-600" />
                                      </div>
                                      <span className="font-medium">Preguntas</span>
                                    </div>
                                    <span className="font-bold text-indigo-600">87 ítems</span>
                                  </div>

                                  <div className="flex items-center justify-between p-2 bg-indigo-50 rounded-lg">
                                    <div className="flex items-center gap-2 text-sm text-neutral-700">
                                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                                        <Clock className="w-4 h-4 text-indigo-600" />
                                      </div>
                                      <span className="font-medium">Duración</span>
                                    </div>
                                    <span className="font-bold text-indigo-600">15-20 min</span>
                                  </div>

                                  {/*<div className="flex items-center justify-between p-2 bg-indigo-50 rounded-lg">
                                    <div className="flex items-center gap-2 text-sm text-neutral-700">
                                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                                        <Target className="w-4 h-4 text-indigo-600" />
                                      </div>
                                      <span className="font-medium">Perfil</span>
                                    </div>
                                    <Badge variant="secondary" className="bg-indigo-100 text-indigo-700 border-indigo-200">
                                      {examenIpv.perfilNombre}
                                    </Badge>
                                  </div>
                                    */}
                                </div>

                                <Button
                                  variant="primary"
                                  size="md"
                                  className={examenesIpvCompletados.length > 0 
                                    ? "w-full bg-neutral-100 text-neutral-400 cursor-not-allowed" 
                                    : "w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
                                  }
                                  onClick={() => iniciarExamenIpv(proceso)}
                                  disabled={examenesIpvCompletados.length > 0}
                                >
                                  <Users className="w-5 h-5" />
                                  {examenesIpvCompletados.length > 0 ? 'Examen Finalizado' : 'Iniciar Examen IPV'}
                                </Button>
                              </div>
                            </motion.div>
                          ))}
                      </div>

                      {/* Mensaje si no hay exámenes ni tests DISC ni IPV */}
                      {getExamenesRavenPorPerfil(perfil).length === 0 && 
                       testsDiscDisponibles.filter(td => td.perfilId === perfil.id).length === 0 &&
                       examenesIpvDisponibles.filter(ipv => ipv.perfilId === perfil.id).length === 0 && (
                        <div className="text-center py-8 bg-neutral-50 rounded-lg">
                          <FileText className="w-12 h-12 text-neutral-400 mx-auto mb-2" />
                          <p className="text-neutral-600">
                            No hay pruebas disponibles en este perfil
                          </p>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 bg-neutral-50 rounded-lg">
                    <BookOpen className="w-12 h-12 text-neutral-400 mx-auto mb-2" />
                    <p className="text-neutral-600">
                      Este proceso no tiene perfiles asignados
                    </p>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
      </div>
    </MobileWarning>
  );
};

export default MisTests;
